<script lang="ts">
  import { editorInstance, activeMarks } from '../../stores/editorStore';
  import { images } from '../../stores/appStore';
  import { i18n } from '../../stores/i18nStore';
  import { callCommand } from '@milkdown/kit/utils';
  import { toggleMark } from '@milkdown/kit/prose/commands';
  import type { CmdKey, Ctx } from '@milkdown/kit/core';
  import type { Image } from '../../types/models';
  import { createMediaReference } from '../../services/imageResolverService';

  // i18n
  $: t = $i18n;

  // Image picker state
  let showImagePicker = false;
  let imagePickerMode: 'gallery' | 'url' | 'confirm' = 'gallery';
  let manualUrl = '';
  let manualAlt = '';
  let selectedImage: Image | null = null;
  let selectedImageAlt = '';

  // Milkdown commands
  import {
    toggleStrongCommand,
    toggleEmphasisCommand,
    toggleInlineCodeCommand,
    wrapInHeadingCommand,
    wrapInBulletListCommand,
    wrapInOrderedListCommand,
    wrapInBlockquoteCommand,
    insertHrCommand,
    turnIntoTextCommand
  } from '@milkdown/kit/preset/commonmark';

  import {
    toggleStrikethroughCommand,
    insertTableCommand
  } from '@milkdown/kit/preset/gfm';

  import { undoCommand, redoCommand } from '@milkdown/kit/plugin/history';
  import { editorViewCtx } from '@milkdown/kit/core';

  // Pandoc essentials commands
  import { toggleSuperscriptCommand } from '@preparo/preset-pandoc-essentials';

  // Custom footnote commands
  import { insertFootnoteCommand, renumberFootnotesCommand } from '../../plugins/footnotePlugin';

  // Types
  interface ToolbarAction {
    id: string;
    icon: string;
    title: string;
    action: () => void;
  }

  // Formatting actions
  const formatting: ToolbarAction[] = [
    { id: 'bold', icon: '𝐁', title: 'Bold (Cmd+B)', action: toggleBold },
    { id: 'italic', icon: '𝐼', title: 'Italic (Cmd+I)', action: toggleItalic },
    { id: 'strikethrough', icon: 'S̶', title: 'Strikethrough', action: toggleStrikethrough },
    { id: 'underline', icon: 'U̲', title: 'Underline', action: toggleUnderline },
    { id: 'smallcaps', icon: 'Sᴄ', title: 'Small Caps', action: toggleSmallCaps },
    { id: 'superscript', icon: 'x²', title: 'Superscript', action: toggleSuperscript },
    { id: 'code', icon: '</>', title: 'Inline Code', action: toggleCode },
  ];

  // Structure actions
  const structure: ToolbarAction[] = [
    { id: 'h1', icon: 'H1', title: 'Heading 1', action: () => setHeading(1) },
    { id: 'h2', icon: 'H2', title: 'Heading 2', action: () => setHeading(2) },
    { id: 'h3', icon: 'H3', title: 'Heading 3', action: () => setHeading(3) },
    { id: 'h4', icon: 'H4', title: 'Heading 4', action: () => setHeading(4) },
    { id: 'paragraph', icon: '¶', title: 'Paragraph', action: setParagraph },
    { id: 'blockquote', icon: '❝', title: 'Quote Block', action: toggleBlockquote },
  ];

  // List actions
  const lists: ToolbarAction[] = [
    { id: 'bullet-list', icon: '•', title: 'Bullet List', action: toggleBulletList },
    { id: 'ordered-list', icon: '1.', title: 'Ordered List', action: toggleOrderedList },
  ];

  // Insertion actions
  const insertions: ToolbarAction[] = [
    { id: 'link', icon: '🔗', title: 'Insert Link (Cmd+K)', action: insertLink },
    { id: 'image', icon: '🖼', title: 'Insert Image', action: insertImage },
    { id: 'code-block', icon: '{ }', title: 'Code Block', action: insertCodeBlock },
    { id: 'table', icon: '⊞', title: 'Insert Table', action: insertTable },
    { id: 'hr', icon: '—', title: 'Horizontal Rule', action: insertHorizontalRule },
    { id: 'footnote', icon: '¹', title: 'Insert Footnote', action: insertFootnote },
    { id: 'renumber', icon: '¹²', title: 'Renumber Footnotes', action: renumberFootnotes },
    { id: 'bibliography', icon: '📚', title: 'Insert Bibliography', action: insertBibliography },
  ];

  // Editing actions
  const editing: ToolbarAction[] = [
    { id: 'undo', icon: '↶', title: 'Undo (Cmd+Z)', action: undo },
    { id: 'redo', icon: '↷', title: 'Redo (Cmd+Shift+Z)', action: redo },
  ];

  // Formatting functions
  function toggleBold() {
    executeCommand(toggleStrongCommand.key);
  }

  function toggleItalic() {
    executeCommand(toggleEmphasisCommand.key);
  }

  function toggleStrikethrough() {
    executeCommand(toggleStrikethroughCommand.key);
  }

  function toggleCode() {
    executeCommand(toggleInlineCodeCommand.key);
  }

  function toggleUnderline() {
    applySpanClass('underline');
  }

  function toggleSmallCaps() {
    applySpanClass('smallcaps');
  }

  function toggleSuperscript() {
    executeCommand(toggleSuperscriptCommand.key);
  }

  /**
   * Apply a text_span mark with the given class to the selection.
   * Uses Prosemirror's toggleMark to properly handle inline formatting.
   */
  function applySpanClass(className: string): void {
    const editor = $editorInstance;
    if (!editor) return;

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { schema } = state;

        // Get the text_span mark type from the schema
        const textSpanType = schema.marks.text_span;
        if (!textSpanType) {
          console.warn('text_span mark type not found in schema');
          return;
        }

        // Use toggleMark with the class attribute
        const command = toggleMark(textSpanType, { class: className });
        command(state, dispatch);
      });
    } catch (error) {
      console.error(`Failed to apply span class .${className}:`, error);
    }
  }

  function setHeading(level: number): void {
    executeCommand(wrapInHeadingCommand.key, level);
  }

  function setParagraph() {
    executeCommand(turnIntoTextCommand.key);
  }

  function toggleBulletList() {
    executeCommand(wrapInBulletListCommand.key);
  }

  function toggleOrderedList() {
    executeCommand(wrapInOrderedListCommand.key);
  }

  function toggleBlockquote() {
    const editor = $editorInstance;
    if (!editor) return;

    let didLift = false;

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { $from, $to } = state.selection;

        // Check if we're inside a blockquote
        let blockquoteDepth = -1;

        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'blockquote') {
            blockquoteDepth = d;
            break;
          }
        }

        if (blockquoteDepth > 0) {
          // We're inside a blockquote → remove it (lift)
          const range = $from.blockRange($to);
          if (range) {
            const tr = state.tr.lift(range, blockquoteDepth - 1);
            dispatch(tr);
            didLift = true;
          }
        }
      });
    } catch (e) {
      console.error('Error in toggleBlockquote:', e);
    }

    // If we didn't lift, add a blockquote
    if (!didLift) {
      executeCommand(wrapInBlockquoteCommand.key);
    }
  }

  function insertLink() {
    const url = prompt('Enter URL:');
    const text = prompt('Link text:') || 'link';
    if (url) {
      insertMarkdown(`[${text}](${url})`);
    }
  }

  function insertImage() {
    // Toggle image picker instead of using prompt
    showImagePicker = !showImagePicker;
    if (showImagePicker) {
      imagePickerMode = 'gallery';
      manualUrl = '';
      manualAlt = '';
      selectedImage = null;
      selectedImageAlt = '';
    }
  }

  function selectProjectImage(image: Image) {
    // Show confirmation step to enter alt text
    selectedImage = image;
    selectedImageAlt = image.altText || '';
    imagePickerMode = 'confirm';
  }

  function confirmInsertImage() {
    if (!selectedImage) return;

    const alt = selectedImageAlt || selectedImage.filename;
    // Use media/filename reference instead of base64 dataUrl
    const mediaRef = createMediaReference(selectedImage.filename);
    insertImageNode(mediaRef, alt);
    closeImagePicker();
  }

  function insertManualImage() {
    if (manualUrl) {
      const alt = manualAlt || 'image';
      insertImageNode(manualUrl, alt);
      closeImagePicker();
    }
  }

  function insertImageNode(src: string, alt: string) {
    const editor = $editorInstance;
    if (!editor) {
      console.warn('Editor not initialized');
      return;
    }

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { schema } = state;

        // Create image node using Milkdown's schema
        const imageType = schema.nodes.image;
        if (!imageType) {
          console.warn('Image node type not found in schema');
          // Fallback to raw markdown
          insertMarkdown(`![${alt}](${src})`);
          return;
        }

        const imageNode = imageType.create({ src, alt, title: alt });
        const transaction = state.tr.replaceSelectionWith(imageNode);
        dispatch(transaction);
      });
    } catch (error) {
      console.error('Failed to insert image:', error);
      // Fallback to raw markdown
      insertMarkdown(`![${alt}](${src})`);
    }
  }

  function backToGallery() {
    selectedImage = null;
    selectedImageAlt = '';
    imagePickerMode = 'gallery';
  }

  function closeImagePicker() {
    showImagePicker = false;
    selectedImage = null;
    selectedImageAlt = '';
    manualUrl = '';
    manualAlt = '';
  }

  function insertCodeBlock() {
    insertMarkdown('\n```\n// Your code here\n```\n');
  }

  function insertFootnote() {
    const noteText = prompt('Footnote text:');
    if (noteText !== null) {
      // Use the native footnote plugin command
      executeCommand(insertFootnoteCommand.key, noteText);
    }
  }

  function renumberFootnotes() {
    // Manual renumbering of footnotes according to their order of appearance
    executeCommand(renumberFootnotesCommand.key);
  }

  function insertBibliography() {
    // Insert bibliography placeholder at cursor position
    insertMarkdown('{{bibliography}}');
  }

  // Helper to insert Markdown text
  function insertMarkdown(text: string): void {
    const editor = $editorInstance;
    if (!editor) {
      console.warn('Editor not initialized');
      return;
    }

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const transaction = state.tr.insertText(text);
        dispatch(transaction);
      });
    } catch (error) {
      console.error('Failed to insert markdown:', error);
    }
  }

  function insertTable() {
    executeCommand(insertTableCommand.key);
  }

  function insertHorizontalRule() {
    executeCommand(insertHrCommand.key);
  }

  function undo() {
    executeCommand(undoCommand.key);
  }

  function redo() {
    executeCommand(redoCommand.key);
  }

  // Execute a Milkdown command
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function executeCommand(commandKey: string | CmdKey<any>, payload?: unknown): void {
    const editor = $editorInstance;
    if (!editor) {
      console.warn('Editor not initialized');
      return;
    }

    try {
      editor.action(callCommand(commandKey, payload));
    } catch (error) {
      console.error(`Failed to execute command ${commandKey}:`, error);
    }
  }
</script>

<div class="toolbar-container">
<div class="editor-toolbar">
  <!-- Formatting section -->
  <div class="toolbar-section">
    {#each formatting as action}
      <button
        class="toolbar-btn"
        class:active={$activeMarks[action.id]}
        title={action.title}
        on:click={action.action}
      >
        {action.icon}
      </button>
    {/each}
  </div>

  <div class="toolbar-divider"></div>

  <!-- Structure section -->
  <div class="toolbar-section">
    {#each structure as action}
      <button
        class="toolbar-btn"
        title={action.title}
        on:click={action.action}
      >
        {action.icon}
      </button>
    {/each}
  </div>

  <div class="toolbar-divider"></div>

  <!-- Lists section -->
  <div class="toolbar-section">
    {#each lists as action}
      <button
        class="toolbar-btn"
        title={action.title}
        on:click={action.action}
      >
        {action.icon}
      </button>
    {/each}
  </div>

  <div class="toolbar-divider"></div>

  <!-- Insertions section -->
  <div class="toolbar-section">
    {#each insertions as action}
      <button
        class="toolbar-btn"
        title={action.title}
        on:click={action.action}
      >
        {action.icon}
      </button>
    {/each}
  </div>

  <div class="toolbar-divider"></div>

  <!-- Editing section -->
  <div class="toolbar-section">
    {#each editing as action}
      <button
        class="toolbar-btn"
        title={action.title}
        on:click={action.action}
      >
        {action.icon}
      </button>
    {/each}
  </div>
</div>

<!-- Image Picker Dropdown -->
{#if showImagePicker}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="image-picker-overlay" on:click={closeImagePicker}></div>
  <div class="image-picker">
    <div class="picker-header">
      <div class="picker-tabs">
        <button
          class="picker-tab"
          class:active={imagePickerMode === 'gallery'}
          on:click={() => imagePickerMode = 'gallery'}
        >
          🖼️ {t('imagePicker.gallery')}
        </button>
        <button
          class="picker-tab"
          class:active={imagePickerMode === 'url'}
          on:click={() => imagePickerMode = 'url'}
        >
          🔗 {t('imagePicker.url')}
        </button>
      </div>
      <button class="picker-close" on:click={closeImagePicker}>✕</button>
    </div>

    {#if imagePickerMode === 'gallery'}
      <div class="picker-content">
        {#if $images.length === 0}
          <div class="picker-empty">
            <span class="picker-empty-icon">🖼️</span>
            <p>{t('imagePicker.noImages')}</p>
            <p class="picker-empty-hint">{t('imagePicker.noImagesHint')}</p>
          </div>
        {:else}
          <div class="picker-gallery">
            {#each $images as image}
              <button
                class="picker-image-btn"
                on:click={() => selectProjectImage(image)}
                title={image.filename}
              >
                <img src={image.dataUrl} alt={image.altText || image.filename} />
                <span class="picker-image-name">{image.filename}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {:else if imagePickerMode === 'confirm' && selectedImage}
      <div class="picker-content picker-confirm">
        <div class="picker-preview">
          <img src={selectedImage.dataUrl} alt={selectedImage.filename} />
        </div>
        <div class="picker-field">
          <label for="selected-image-alt">{t('imagePicker.altLabel')}</label>
          <input
            id="selected-image-alt"
            type="text"
            bind:value={selectedImageAlt}
            placeholder={t('imagePicker.altPlaceholder')}
          />
        </div>
        <div class="picker-actions">
          <button class="picker-back-btn" on:click={backToGallery}>
            ← {t('imagePicker.back')}
          </button>
          <button class="picker-insert-btn" on:click={confirmInsertImage}>
            {t('imagePicker.insert')}
          </button>
        </div>
      </div>
    {:else}
      <div class="picker-content picker-url-form">
        <div class="picker-field">
          <label for="image-url">{t('imagePicker.urlLabel')}</label>
          <input
            id="image-url"
            type="url"
            bind:value={manualUrl}
            placeholder="https://example.com/image.png"
          />
        </div>
        <div class="picker-field">
          <label for="image-alt">{t('imagePicker.altLabel')}</label>
          <input
            id="image-alt"
            type="text"
            bind:value={manualAlt}
            placeholder={t('imagePicker.altPlaceholder')}
          />
        </div>
        <button
          class="picker-insert-btn"
          on:click={insertManualImage}
          disabled={!manualUrl}
        >
          {t('imagePicker.insert')}
        </button>
      </div>
    {/if}
  </div>
{/if}
</div>

<style>
  .toolbar-container {
    position: relative;
  }

  .editor-toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--toolbar-background, #f9fafb);
    border-bottom: 1px solid var(--border-color, #e5e7eb);
    overflow-x: auto;
    flex-wrap: wrap;
  }

  .toolbar-section {
    display: flex;
    gap: 0.25rem;
  }

  .toolbar-divider {
    width: 1px;
    height: 24px;
    background: var(--border-color, #e5e7eb);
  }

  .toolbar-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 0.5rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-primary, #1f2937);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .toolbar-btn:hover {
    background: var(--hover-background, #f3f4f6);
    border-color: var(--border-color, #e5e7eb);
  }

  .toolbar-btn:active {
    background: var(--active-background, #e5e7eb);
  }

  .toolbar-btn.active {
    background: var(--accent-color-light, #dbeafe);
    border-color: var(--accent-color, #3b82f6);
    color: var(--accent-color, #3b82f6);
  }

  .toolbar-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .editor-toolbar {
      padding: 0.5rem;
      gap: 0.25rem;
    }

    .toolbar-btn {
      min-width: 28px;
      height: 28px;
      font-size: 0.75rem;
    }

    .toolbar-divider {
      height: 20px;
    }
  }

  /* Dark mode */
  @media (prefers-color-scheme: dark) {
    .editor-toolbar {
      --toolbar-background: #1f2937;
      --border-color: #374151;
      --text-primary: #f9fafb;
      --hover-background: #374151;
      --active-background: #4b5563;
      --accent-color: #60a5fa;
      --accent-color-light: #1e3a8a;
    }
  }

  /* Image Picker Styles */
  .image-picker-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 999;
  }

  .image-picker {
    position: absolute;
    top: 100%;
    left: 1rem;
    right: 1rem;
    max-width: 500px;
    max-height: 400px;
    background: var(--panel-background, #ffffff);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color, #e5e7eb);
    background: var(--toolbar-background, #f9fafb);
  }

  .picker-tabs {
    display: flex;
    gap: 0.25rem;
  }

  .picker-tab {
    padding: 0.5rem 1rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    color: var(--text-secondary, #6b7280);
    transition: all 0.15s ease;
  }

  .picker-tab:hover {
    background: var(--hover-background, #f3f4f6);
  }

  .picker-tab.active {
    background: var(--accent-color, #3b82f6);
    color: white;
  }

  .picker-close {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    color: var(--text-secondary, #6b7280);
  }

  .picker-close:hover {
    background: var(--hover-background, #f3f4f6);
    color: var(--text-primary, #1f2937);
  }

  .picker-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .picker-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    color: var(--text-secondary, #6b7280);
  }

  .picker-empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .picker-empty p {
    margin: 0;
  }

  .picker-empty-hint {
    font-size: 0.75rem;
    margin-top: 0.5rem !important;
    opacity: 0.7;
  }

  .picker-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.75rem;
  }

  .picker-image-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
    background: var(--hover-background, #f3f4f6);
    border: 2px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .picker-image-btn:hover {
    border-color: var(--accent-color, #3b82f6);
  }

  .picker-image-btn img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
  }

  .picker-image-name {
    margin-top: 0.5rem;
    font-size: 0.7rem;
    color: var(--text-secondary, #6b7280);
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .picker-url-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .picker-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .picker-field label {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-secondary, #6b7280);
  }

  .picker-field input {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 4px;
    font-size: 0.875rem;
    background: var(--input-background, #ffffff);
    color: var(--text-primary, #1f2937);
  }

  .picker-field input:focus {
    outline: none;
    border-color: var(--accent-color, #3b82f6);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  .picker-insert-btn {
    padding: 0.75rem 1rem;
    background: var(--accent-color, #3b82f6);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .picker-insert-btn:hover:not(:disabled) {
    background: var(--accent-color-dark, #2563eb);
  }

  .picker-insert-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Confirm mode styles */
  .picker-confirm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .picker-preview {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
    background: var(--hover-background, #f3f4f6);
    border-radius: 8px;
  }

  .picker-preview img {
    max-width: 100%;
    max-height: 150px;
    object-fit: contain;
    border-radius: 4px;
  }

  .picker-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;
  }

  .picker-back-btn {
    padding: 0.75rem 1rem;
    background: transparent;
    color: var(--text-secondary, #6b7280);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .picker-back-btn:hover {
    background: var(--hover-background, #f3f4f6);
    color: var(--text-primary, #1f2937);
  }

  /* Dark mode for image picker */
  @media (prefers-color-scheme: dark) {
    .image-picker {
      --panel-background: #1f2937;
      --border-color: #374151;
      --text-primary: #f9fafb;
      --text-secondary: #9ca3af;
      --hover-background: #374151;
      --input-background: #111827;
      --accent-color: #60a5fa;
      --accent-color-dark: #3b82f6;
    }
  }
</style>
